"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { cva } from "class-variance-authority";
import NextLink from "next/link";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  Checkbox,
  Form,
  Heading,
  Modal,
  Select,
  Text,
  TextField,
} from "ui-components";

import { makeToast } from "@/components/Toast/Toast";
import {
  UserUpdateProfileDtoCountry,
  useOrganizationGetWorkPrideBrochure,
  UserUpdateProfileDtoPronoun,
  CreateWorkPrideDtoSizeOfOrganization,
  CreateWorkPrideDtoNumberOfVacancies,
  CreateWorkPrideDtoWantToReachOut,
  WorkprideBrochureResponse,
} from "@/hooks/api/generated";
import { UserPronouns } from "@/src/app/[locale]/(public)/workpride/components/UserPronouns";
import { SearchLocation } from "@/src/components/SearchLocation/SearchLocation";
import { parseLocation } from "@/src/utils/location";
import { createSalesforceLeadWorkPride } from "@/utils/salesForce";

import {
  DownloadBrochureSchemaType,
  downloadBrochureSchema,
} from "../schema/download-brochure";

import { InterestedInBecoming } from "./InterestedInBecoming";
import { IWantToReachOut } from "./IWantToReachOut";

interface IDownloadBrochureProps {
  isEnquireWhite?: boolean;
  isEnquireBlue?: boolean;
  buttonText?: string;
}

// Define the CVA configuration
const enquireClass = cva("", {
  variants: {
    state: {
      white:
        "!bg-[#FFFFFF] inline-flex items-center justify-center gap-[12.57px] w-[300px] h-[50px] md:w-auto md:h-auto rounded-[56.58px] px-5 py-4 md:px-10 md:py-6 outline outline-[2px] md:outline-[3px] outline-[#282120] outline-offset-[-2px] md:outline-offset-[-3px] text-[#282120] font-[500] break-words text-[16px] md:text-[18px]",
      blue: "!bg-[#F4BF43] text-[#282120] py-6 !px-12 border-[#282120] border-2 leading-3 mx-auto focus:outline-none !text-lg",
      default:
        "inline-flex items-center justify-center gap-3 w-[300px] md:w-[453px] h-[50px] md:h-auto rounded-[57px] !bg-[#F4BF43] px-5 py-4 md:px-10 md:py-[22px] outline outline-[2px] md:outline-[3px] outline-[#282120] outline-offset-[-2px] md:outline-offset-[-3px] text-[#282120] font-[500] break-words text-[16px] md:text-lg ",
    },
  },
  defaultVariants: {
    state: "default",
  },
});

export default function DownloadBrochure({
  isEnquireWhite,
  isEnquireBlue,
  buttonText,
}: Readonly<IDownloadBrochureProps>): JSX.Element {
  const [isShowModal, setIsShowModal] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { mutate: download, isPending: isDownloadOrganisationBrochureLoading } =
    useOrganizationGetWorkPrideBrochure();
  const state = isEnquireWhite ? "white" : isEnquireBlue ? "blue" : "default";

  const CreateWorkPrideDtoSizeOfOrganizationOptions = Object.entries(
    CreateWorkPrideDtoSizeOfOrganization
  ).map(([, value]) => ({
    id: value,
    title: value,
  }));

  const CreateWorkPrideDtoNumberOfVacanciesOptions = Object.entries(
    CreateWorkPrideDtoNumberOfVacancies
  ).map(([, value]) => ({
    id: value,
    title: value,
  }));

  const {
    register,
    handleSubmit,
    getFieldState,
    setValue,
    reset,
    watch,
    setError,
    formState: { errors },
  } = useForm<DownloadBrochureSchemaType>({
    resolver: zodResolver(downloadBrochureSchema),
    defaultValues: {
      is_hiring: undefined,
      privacyPolicy: false,
    },
  });
  const isHiring = watch("is_hiring");
  const onSubmit = (data: DownloadBrochureSchemaType): void => {
    const location = parseLocation(data.location);

    download(
      {
        data: {
          ...data,
          country: location?.countryCode ?? UserUpdateProfileDtoCountry.GB,
          location: location?.fullLocation,
          number_of_vacancies:
            data.number_of_vacancies as CreateWorkPrideDtoNumberOfVacancies,
          size_of_organization:
            data.size_of_organization as CreateWorkPrideDtoSizeOfOrganization,
          want_to_reach_out:
            data.want_to_reach_out as CreateWorkPrideDtoWantToReachOut,
        },
      },
      {
        onSuccess: (res: WorkprideBrochureResponse) => {
          reset({
            privacyPolicy: false,
            location: "",
            first_name: "",
            last_name: "",
            organization: "",
            job_title: "",
            email: "",
            is_hiring: undefined,
            size_of_organization: undefined,
            number_of_vacancies: undefined,
            want_to_reach_out: undefined,
            pronoun: undefined,
            interested_in_becoming: undefined,
          });
          setIsSubmitted(true);
          createSalesforceLeadWorkPride(data, res.workpride);
        },
        onError: () => {
          makeToast({ title: "Something went wrong.", intent: "danger" });
        },
      }
    );
  };
  return (
    <div>
      <Button
        className={`${enquireClass({ state })} whitespace-normal`}
        onPress={() => {
          setIsShowModal(!isShowModal);
          setIsSubmitted(false);
        }}
      >
        {buttonText ||
          (isEnquireWhite ? (
            "Register your interest"
          ) : (
            <>
              <div className="block md:hidden">
                Register your interest for
                <br />
                WorkPride 2027
              </div>
              <div className="hidden md:block">
                Register your interest for WorkPride 2027
              </div>
            </>
          ))}
      </Button>
      <Modal
        animatedModalClassName="md:min-w-[600px] overflow-y-auto h-[80vh] flex flex-1 items-center justify-center"
        isOpen={isShowModal}
        onOpenChange={setIsShowModal}
      >
        {isSubmitted ? (
          <div className="flex flex-col gap-y-5 p-10 justify-center items-center w-3/4">
            <Text
              align="center"
              className="text-[18px] text-center text-black font-normal tracking-[0.18px] break-words font-red-hat-display"
            >
              Thanks for getting in touch!
            </Text>
            <Text
              align="center"
              className="text-[18px] text-center text-black font-normal tracking-[0.18px] break-words font-red-hat-display"
            >
              We will notify you when registration opens for WorkPride 2027.
              <br />
              <br />
              Our team will reach out shortly if you select becoming a sponsor
              or speaker.
            </Text>
          </div>
        ) : (
          <div className="flex flex-col w-full h-full py-[20px] px-[10px]">
            <Heading align="center" className="px-8 md:px-10">
              Register your interest for WorkPride 2027
            </Heading>
            <Text
              align="center"
              className="px-[24px] md:px-[40px] leading-5 mt-5 !text-[14px] md:!text-[18px] font-normal"
            >
              Get in touch to be the first notified when registration opens for
              WorkPride 2027.
            </Text>
            <Form
              className="flex flex-col px-3 py-10 md:p-10 md:y-10"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                <TextField
                  placeholder="First Name"
                  label="First Name"
                  isRequired
                  register={register("first_name")}
                  errorMessage={errors.first_name?.message}
                  hasValue={getFieldState("first_name").isDirty}
                />
                <TextField
                  placeholder="Last Name"
                  label="Last Name"
                  isRequired
                  register={register("last_name")}
                  errorMessage={errors.last_name?.message}
                  hasValue={getFieldState("last_name").isDirty}
                />
              </div>

              <UserPronouns
                label="Pronouns"
                isRequired
                selectPronoun={watch("pronoun")}
                onPronounChange={(pronoun) =>
                  setValue("pronoun", pronoun as UserUpdateProfileDtoPronoun)
                }
                errorMessage={errors.pronoun?.message}
              />
              <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                <TextField
                  placeholder="Email"
                  label="Email"
                  isRequired
                  register={register("email")}
                  errorMessage={errors.email?.message}
                  hasValue={getFieldState("email").isDirty}
                />
                <SearchLocation
                  placeholder="Location"
                  label="Location"
                  size="full"
                  isRequired
                  onSelectAddress={(address) => {
                    setValue("location", address);
                    setError("location", { message: "" });
                  }}
                  errorMessage={errors.location?.message}
                  hasValue={getFieldState("location").isDirty}
                  defaultValue={watch("location")}
                />
                <TextField
                  placeholder="Organization"
                  label="Organization"
                  isRequired
                  register={register("organization")}
                  errorMessage={errors.organization?.message}
                  hasValue={getFieldState("organization").isDirty}
                />
                <TextField
                  placeholder="Job Title"
                  label="Job Title"
                  register={register("job_title")}
                  errorMessage={errors.job_title?.message}
                  hasValue={getFieldState("job_title").isDirty}
                />
              </div>
              {/* <div className="flex justify-start">
              <Checkbox
                label="Hiring"
                isSelected={isHiring}
                onChange={(isSelected) => {
                  if (isSelected) {
                    setValue("is_hiring", true);
                  } else {
                    setValue("is_hiring", false);
                  }
                }}
              />
            </div> */}
              <InterestedInBecoming
                label="I’m interested in becoming a"
                isRequired
                selectInterestedInBecoming={watch("interested_in_becoming")}
                onInterestedInBecomingChange={(interested_in_becoming) =>
                  setValue("interested_in_becoming", interested_in_becoming)
                }
                errorMessage={errors.interested_in_becoming?.message}
              />
              <div className="gap-x-5 w-full mb-4">
                <Select
                  size="full"
                  className="w-full"
                  label="Are you hiring?"
                  placeholder="Please select"
                  options={[
                    { id: "true", title: "Yes" },
                    { id: "false", title: "No" },
                  ]}
                  errorMessage={errors.is_hiring?.message}
                  isRequired
                  onSelection={(selectedValue) => {
                    if (selectedValue === "true") {
                      setValue("is_hiring", true);
                    } else if (selectedValue === "false") {
                      setValue("is_hiring", false);
                    }
                  }}
                />
              </div>
              {isHiring === true && (
                <>
                  <div className="flex flex-col md:flex-row gap-x-5">
                    <div className="w-full md:w-1/2">
                      <Select
                        size="full"
                        label="Size of your organization"
                        placeholder="Size of your organization"
                        options={CreateWorkPrideDtoSizeOfOrganizationOptions}
                        errorMessage={errors.size_of_organization?.message}
                        isRequired={isHiring}
                        onSelection={(selectedValue) => {
                          const value = selectedValue?.toString();
                          if (value) {
                            setValue(
                              "size_of_organization",
                              value as CreateWorkPrideDtoSizeOfOrganization
                            );
                          }
                        }}
                      />
                    </div>
                    <div className="w-full md:w-1/2">
                      <Select
                        size="full"
                        label="Current number of vacancies"
                        placeholder="Current number of vacancies"
                        options={CreateWorkPrideDtoNumberOfVacanciesOptions}
                        errorMessage={errors.number_of_vacancies?.message}
                        isRequired={isHiring}
                        onSelection={(selectedValue) => {
                          const value = selectedValue?.toString();
                          if (value) {
                            setValue(
                              "number_of_vacancies",
                              value?.toString() as CreateWorkPrideDtoNumberOfVacancies
                            );
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col md:flex-row gap-x-5 mb-4">
                    <IWantToReachOut
                      label="I want to reach out to:"
                      isRequired={isHiring}
                      selectIWantToReachOut={watch("want_to_reach_out")}
                      onIWantToReachOutChange={(want_to_reach_out) => {
                        setValue(
                          "want_to_reach_out",
                          want_to_reach_out as CreateWorkPrideDtoWantToReachOut
                        );
                      }}
                      errorMessage={errors.want_to_reach_out?.message}
                    />
                  </div>
                </>
              )}

              <div className="flex justify-end">
                <Checkbox
                  label={
                    <>
                      I confirm that I have read and accept the{" "}
                      <NextLink
                        className="underline"
                        href="/terms-of-use"
                        target="_blank"
                        onClick={(event) => event.stopPropagation()}
                      >
                        Terms
                      </NextLink>
                      . By filling out this form, I agree I have read and
                      understand the{" "}
                      <NextLink
                        className="underline"
                        href="/privacy-policy"
                        target="_blank"
                        onClick={(event) => event.stopPropagation()}
                      >
                        Privacy Policy
                      </NextLink>
                    </>
                  }
                  onChange={(value) => {
                    setValue("privacyPolicy", value);
                  }}
                  errorMessage={errors.privacyPolicy?.message}
                />
              </div>
              <Button
                type="submit"
                className="mt-5 mx-auto"
                isLoading={isDownloadOrganisationBrochureLoading}
                isDisabled={isDownloadOrganisationBrochureLoading}
              >
                Get in Touch
              </Button>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
}
